'use client'

import type { ReactNode } from 'react'

import { Icon } from '@graftcode/design-system'

import { ErrorBoundary, MainContentHeader, UnauthenticatedBanner } from '@common/core/components'

import { ErrorState } from '@/core/components/ErrorState'
import { GatewayConnectionSettings } from '@/core/components/GatewaySelector/GatewayConnectionSettings'
import { useIsLocalGatewayConfigurationMode } from '@/core/hooks/useIsLocalGatewayConfigurationMode'

import { VISION_MAIN_CONTENT } from '../../const'

import styles from '../../Layout.module.scss'

interface LayoutMainContentProps {
  children: ReactNode
  isError: boolean
  isLoading: boolean
  isVisionLocal: boolean
  pathString: string
  onRefetchGateway: () => Promise<unknown>
  onRefresh: () => void
  onCreateGateway: () => void
  onSignUpClick: () => void
}

export function LayoutMainContent({
  children,
  isError,
  isLoading,
  isVisionLocal,
  pathString,
  onRefetchGateway,
  onRefresh,
  onCreateGateway,
  onSignUpClick,
}: LayoutMainContentProps) {
  const showLocalGatewayConfiguration = useIsLocalGatewayConfigurationMode()
  const showGatewayRecovery = isError && showLocalGatewayConfiguration

  return (
    <main className="content">
      {isError ? (
        <>
          <ErrorState onRetry={onRefresh} />
          {showGatewayRecovery ? (
            <div className={styles.gatewayErrorRecovery}>
              <GatewayConnectionSettings onRefetchGateway={onRefetchGateway} variant="button" />
            </div>
          ) : null}
          {!isLoading && isVisionLocal && <UnauthenticatedBanner onSignUpClick={onSignUpClick} />}
        </>
      ) : (
        <>
          {!pathString && (
            <div className={styles.contentHeader}>
              <MainContentHeader
                title={VISION_MAIN_CONTENT.title}
                description={VISION_MAIN_CONTENT.description}
                actionButtonLabel={VISION_MAIN_CONTENT.actionButtonLabel}
                actionButtonIcon={Icon.AddRegular}
                showActionButton={!isVisionLocal}
                onRefreshClick={onRefresh}
                onActionClick={onCreateGateway}
              />
            </div>
          )}
          <ErrorBoundary>{children}</ErrorBoundary>
          {!isLoading && <UnauthenticatedBanner onSignUpClick={onSignUpClick} />}
        </>
      )}
    </main>
  )
}
